import { useState } from "react";
import { Box, Button, FormControlLabel, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import IconRejected from "@components/Images/IconRejected";
import { CustomSwitch } from "@components/CustomSwitch";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { usePostValidateNextStageMutation } from "@api/applications";
import { useBuroReconsultationMutation } from "@api/sat_apis_new/satApsi";
import { STATUS_CATALOG } from "@helpers/constantes";
import { setModalErrorVisible, setProcessPage } from "@store/slices/appSlice";
import { setCheckSatBureau } from "@store/slices/registerSlice";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@interfaces/response";
import useInterfaceConfig from "@hooks/useInterfaceConfig";

const BuroBloqueado = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const interfaceConfig = useInterfaceConfig();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const shareholderData = useSelector((state: RootState) => state.register.shareholder);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [confirmUnlock, setConfirmUnlock] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [buroReconsultation] = useBuroReconsultationMutation();

  const handleClick = async () => {
    try {
      setIsLoading(true);
      const payLoad = {
        rfc: shareholderData.rfc + shareholderData.rfc_homoclave,
        loan_application_id: applicationData.id,
        application_code: applicationData.company_application_code,
        person_type: "PF",
      };
      await buroReconsultation(payLoad).unwrap();

      const next_status = interfaceConfig?.status_waiting_room || STATUS_CATALOG.waiting_room.code;
      const next_url = interfaceConfig?.url_waiting_room || STATUS_CATALOG.waiting_room.url;

      await triggerPostNextStage({
        applicationId: applicationData.id,
        statusCode: next_status,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setProcessPage(STATUS_CATALOG.waiting_room.url));
      dispatch(setCheckSatBureau({ check_sat_bureau: true }));
      navigate(next_url);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "ReConsultaBuro",
          values: {
            rfc: shareholderData.rfc,
            loan_application_id: applicationData.id,
            application_code: applicationData.company_application_code,
          },
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <InactivityHandler />
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            color={theme?.palette?.primary?.main}
            fontWeight={500}
            align="center"
            width={isMobileOrTablet ? "90%" : "60%"}
          >
            {interfaceConfig.textos_tofu?.pm?.buro_bloqueado?.titulo ||
              "Consulta de Buró de Crédito del Accionista Bloqueada"}
          </Typography>

          <Typography
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            align="center"
            width={isMobileOrTablet ? "90%" : "60%"}
            mt={3}
          >
            Para continuar con tu solicitud, necesitamos que desbloquees el acceso a tu Buró de Crédito. Puedes hacerlo
            fácilmente desde el portal oficial en el siguiente enlace:
          </Typography>

          <Typography
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            align="center"
            mt={3}
            component={"a"}
            href="https://www.burodecredito.com.mx/personas-físicas/productos/bloqueo.html"
            target="_blank"
            textAlign={"center"}
            width={isMobileOrTablet ? "90%" : "60%"}
          >
            https://www.burodecredito.com.mx/personas-físicas/productos/bloqueo.html
          </Typography>

          <Box display="flex" justifyContent="center" mt={5}>
            <IconRejected width={isMobileOrTablet ? "150" : "200"} fill={theme.palette.primary.light} />
          </Box>

          <Typography
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            align="center"
            mt={3}
            width={isMobileOrTablet ? "90%" : "60%"}
          >
            Una vez que hayas realizado el desbloqueo, podremos continuar con el análisis de tu solicitud de crédito.
          </Typography>

          <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 2 : 3} textAlign="center">
            <FormControlLabel
              control={<CustomSwitch />}
              label={
                <Typography variant={"body2"} color={theme?.palette?.primary?.dark}>
                  Ya desbloqueé mi Buró de Crédito
                </Typography>
              }
              name="switch"
              checked={confirmUnlock}
              onChange={() => setConfirmUnlock(!confirmUnlock)}
            />
          </Box>

          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="default"
              disabled={!confirmUnlock || isLoading}
              onClick={handleClick}
              sx={{ width: "130px" }}
            >
              Continuar
            </Button>
          </Box>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default BuroBloqueado;
